/* eslint-disable react/no-unescaped-entities */
import { Calculator, Handshake, ShieldCheck, Lightbulb, Leaf, ChevronDown } from 'lucide-react';
import { PageHero, SectionHeading } from '../components/SectionHeading';
import { ScrollReveal, Stagger, StaggerItem } from '../components/Motion';
import { ASSETS, VALUES, ADVANTAGES, pick } from '../data/content';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useLang } from '../i18n/LanguageContext';

const IconMap = { Calculator, Handshake, ShieldCheck, Lightbulb, Leaf };

const useIdentity = (t) => [
    [t('about.id_parent_group'), 'Delta International Group Ltd.'],
    [t('about.id_energy_division'), 'Delta International Energy'],
    [t('about.id_operating'), 'Saudi Delta Energy Company (SDEC)'],
    [t('about.id_ownership'), t('about.id_ownership_val')],
    [t('about.id_founded'), '1978'],
    [t('about.id_hq'), t('about.id_hq_val')],
    [t('about.id_website'), 'www.saudideltaenergy.com'],
    [t('about.id_email'), 'info@saudideltaenergy.com / info@deltagroup-intl.com'],
];

const About = () => {
    const { t, lang } = useLang();
    const IDENTITY = useIdentity(t);
    return (
        <div data-testid="about-page">
            <PageHero
                eyebrow={t('about.hero_eyebrow')}
                title={t('about.hero_title')}
                sub={t('about.hero_sub')}
                image={ASSETS.desert}
            />

            {/* The Company */}
            <section className="bg-cream py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
                    <ScrollReveal className="lg:col-span-5">
                        <div className="eyebrow mb-5">{t('about.company_eyebrow')}</div>
                        <h2 className="editorial-h2 text-navy text-3xl md:text-4xl">{t('about.company_h2')}</h2>
                    </ScrollReveal>
                    <ScrollReveal delay={0.1} className="lg:col-span-7">
                        <p className="text-ink/80 leading-relaxed text-base md:text-lg">
                            {t('about.company_body_1')}
                        </p>
                        <p className="text-ink/80 leading-relaxed mt-5 text-base md:text-lg">
                            {t('about.company_body_2')}
                        </p>

                        <div className="mt-12 border-t border-gold/30">
                            <div className="eyebrow mt-8 mb-6">{t('about.identity_eyebrow')}</div>
                            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5">
                                {IDENTITY.map(([k, v]) => (
                                    <div key={k} className="border-b border-navy/10 pb-3">
                                        <dt className="text-[10px] tracking-[0.2em] uppercase text-ink/50">{k}</dt>
                                        <dd className="text-navy text-sm md:text-base mt-1">{v}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    </ScrollReveal>
                </div>
            </section>

            {/* Mission & Vision */}
            <section className="bg-navy-deep py-24 md:py-32 grain-overlay">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-12">
                    {[
                        { label: t('about.mission_label'), body: t('about.mission_body') },
                        { label: t('about.vision_label'), body: t('about.vision_body') },
                    ].map((m, i) => (
                        <ScrollReveal key={m.label} delay={i * 0.1}>
                            <div className="border border-gold/20 p-10 md:p-14 h-full">
                                <div className="eyebrow mb-6">{m.label}</div>
                                <p className="font-display text-white italic text-xl md:text-2xl leading-snug">
                                    "{m.body}"
                                </p>
                            </div>
                        </ScrollReveal>
                    ))}
                </div>
            </section>

            {/* Values */}
            <section className="bg-white py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <SectionHeading
                        eyebrow={t('about.values_eyebrow')}
                        title={t('about.values_h2')}
                        sub={t('about.values_sub')}
                        align="center"
                        className="mb-16"
                    />
                    <Stagger className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-px bg-gold/15">
                        {VALUES.map((v) => {
                            const Icon = IconMap[v.icon];
                            return (
                                <StaggerItem key={v.title} testid={`value-${v.title.toLowerCase().replace(/\s+/g, '-')}`} className="bg-navy-deep p-8 md:p-9 group hover:bg-navy-mid transition-colors">
                                    <div className="w-12 h-12 border border-gold/40 flex items-center justify-center mb-6 group-hover:border-gold transition-colors">
                                        {Icon && <Icon className="w-5 h-5 text-gold" />}
                                    </div>
                                    <h3 className="font-display text-gold text-xl mb-3">{pick(v, 'title', lang)}</h3>
                                    <p className="text-white/65 text-sm leading-relaxed">{pick(v, 'desc', lang)}</p>
                                </StaggerItem>
                            );
                        })}
                    </Stagger>
                </div>
            </section>

            {/* Competitive Advantages */}
            <section className="bg-cream py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
                    <ScrollReveal className="lg:col-span-4">
                        <div className="eyebrow mb-5">{t('about.adv_eyebrow')}</div>
                        <h2 className="editorial-h2 text-navy text-3xl md:text-4xl">{t('about.adv_h2')}</h2>
                        <p className="mt-6 text-ink/70 leading-relaxed">
                            {t('about.adv_sub')}
                        </p>
                    </ScrollReveal>
                    <ScrollReveal delay={0.1} className="lg:col-span-8">
                        <Accordion type="single" collapsible defaultValue="adv-0" data-testid="advantages-accordion">
                            {ADVANTAGES.map((a, i) => (
                                <AccordionItem key={a.title} value={`adv-${i}`} className="border-b border-navy/15">
                                    <AccordionTrigger data-testid={`advantage-${i}`} className="py-6 hover:no-underline group">
                                        <div className="flex items-center gap-6 text-left">
                                            <span className="metric-number text-xl md:text-2xl w-12">{String(i + 1).padStart(2, '0')}</span>
                                            <span className="font-display text-navy text-lg md:text-2xl">{pick(a, 'title', lang)}</span>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="text-ink/75 text-base leading-relaxed pl-[72px] pb-7">
                                        {pick(a, 'desc', lang)}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </ScrollReveal>
                </div>
            </section>

            {/* The Delta Group */}
            <section className="bg-white py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <SectionHeading
                        eyebrow={t('about.group_eyebrow')}
                        title={t('about.group_h2')}
                        sub={t('about.group_sub')}
                        align="center"
                        className="mb-20"
                    />
                    <div className="max-w-4xl mx-auto">
                        <div className="border border-navy/15 bg-cream px-10 py-8 text-center">
                            <div className="eyebrow mb-2">{t('about.parent_label')}</div>
                            <div className="font-display text-navy text-2xl md:text-3xl">Delta International Group Ltd.</div>
                        </div>
                        <div className="flex justify-center my-6">
                            <ChevronDown className="w-6 h-6 text-gold" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gold/20 border border-gold/20">
                            {[
                                { name: t('about.group_delta_energy'), desc: t('about.group_delta_energy_desc'), accent: true },
                                { name: t('about.group_delta_real'), desc: t('about.group_delta_real_desc') },
                                { name: t('about.group_delta_ind'), desc: t('about.group_delta_ind_desc') },
                            ].map((b) => (
                                <div key={b.name} className={`p-8 text-center ${b.accent ? 'bg-navy-deep' : 'bg-cream'}`}>
                                    <div className={`font-display text-xl md:text-2xl ${b.accent ? 'text-gold' : 'text-navy'}`}>
                                        {b.name}
                                    </div>
                                    <div className={`mt-2 text-[11px] tracking-widest uppercase ${b.accent ? 'text-white/60' : 'text-ink/55'}`}>
                                        {b.desc}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default About;
